import type { CSSProperties } from 'react'
import type { SxProps, Theme } from '@mui/material'

const padBase = {
  width: '100%',
  height: (theme: Theme) => theme.spacing(22.5),
  borderRadius: 1,
  borderStyle: 'solid',
  borderWidth: 1,
  backgroundColor: 'gray.3',
  overflow: 'hidden',
}

const sectionCard = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  p: 2,
  borderRadius: 2,
  border: 1,
  borderColor: 'gray.12',
  backgroundColor: 'background.paper',
}

export const styles = {
  backRow: {
    display: 'flex',
    '& .MuiButton-root': { px: 0, minWidth: 0 },
  },
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'gray.4',
    px: { xs: 2, sm: 3 },
    py: { xs: 2, sm: 6 },
    gap: 2,
  },
  card: {
    width: '100%',
    maxWidth: (theme: Theme) => theme.spacing(90),
    p: { xs: 2.5, sm: 4 },
    borderRadius: 2,
    border: 1,
    borderColor: 'gray.12',
    backgroundColor: 'background.paper',
    display: 'flex',
    flexDirection: 'column',
    gap: 2.5,
  },
  banner: {
    width: '100%',
    maxWidth: (theme: Theme) => theme.spacing(90),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    pb: 2,
    borderBottom: 1,
    borderColor: 'gray.8',
  },
  headerSecure: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    color: 'gray.56',
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  tightStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
  },
  muted: {
    color: 'gray.64',
  },
  caption: {
    color: 'gray.56',
  },
  actions: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: 'flex-end',
    gap: 1.5,
  },
  stateWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 1.5,
    py: 3,
  },
  stateIcon: {
    width: (theme: Theme) => theme.spacing(6),
    height: (theme: Theme) => theme.spacing(6),
    color: 'primary.main',
  },
  stateIconWarning: {
    width: (theme: Theme) => theme.spacing(6),
    height: (theme: Theme) => theme.spacing(6),
    color: 'warning.main',
  },
  stateIconSuccess: {
    width: (theme: Theme) => theme.spacing(6),
    height: (theme: Theme) => theme.spacing(6),
    color: 'success.main',
  },
  dobRow: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr 1fr 1.4fr', sm: '1fr 1fr 1.4fr' },
    gap: 1.5,
  },
  dobItem: {
    minWidth: 0,
  },
  fieldset: {
    border: 0,
    p: 0,
    m: 0,
    minWidth: 0,
  },
  section: sectionCard,
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    m: 0,
    p: 0,
    listStyle: 'none',
  },
  itemRow: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'stretch', sm: 'center' },
    justifyContent: 'space-between',
    gap: 1.5,
    py: 1.5,
    borderTop: 1,
    borderColor: 'gray.8',
    '&:first-of-type': { borderTop: 0 },
  },
  itemText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
    minWidth: 0,
  },
  groupHeading: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
  },
  progressTrack: {
    height: (theme: Theme) => theme.spacing(0.75),
    borderRadius: 1,
    backgroundColor: 'gray.8',
    overflow: 'hidden',
  },
  document: {
    maxHeight: (theme: Theme) => theme.spacing(45),
    overflowY: 'auto',
    p: 2,
    borderRadius: 1,
    border: 1,
    borderColor: 'gray.12',
    backgroundColor: 'gray.3',
    typography: 'medium',
    '&:focus-visible': {
      outline: 2,
      outlineColor: 'primary.main',
      outlineStyle: 'solid',
    },
  },
  padHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
  },
  pad: {
    ...padBase,
    borderColor: 'gray.16',
  },
  padError: {
    ...padBase,
    borderColor: 'error.main',
  },
  typedSignature: {
    ...padBase,
    borderColor: 'gray.16',
    display: 'flex',
    alignItems: 'center',
    px: 3,
    fontStyle: 'italic',
    typography: 'header2',
    color: 'gray.80',
  },
  legal: {
    color: 'gray.56',
  },
  terms: {
    m: 0,
    pl: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
    color: 'gray.64',
  },
  receiptRow: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'stretch', sm: 'center' },
    justifyContent: 'space-between',
    gap: 1.5,
    py: 1.5,
    borderTop: 1,
    borderColor: 'gray.8',
    '&:first-of-type': { borderTop: 0 },
  },
  skeleton: {
    height: (theme: Theme) => theme.spacing(9),
    borderRadius: 2,
  },
  lockedCard: {
    width: '100%',
    maxWidth: (theme: Theme) => theme.spacing(60),
    p: { xs: 2.5, sm: 4 },
    borderRadius: 2,
    border: 1,
    borderColor: 'gray.12',
    backgroundColor: 'background.paper',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
} satisfies Record<string, SxProps<Theme>>

export const buildProgressFill = (ratio: number): SxProps<Theme> => ({
  height: '100%',
  width: `${Math.round(Math.min(1, Math.max(0, ratio)) * 100)}%`,
  backgroundColor: 'success.main',
})

export const canvasStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  touchAction: 'none',
}
